import React, { useState } from "react";
import { PrimaryButton } from "@cadmakers-ui/core";
import { Icon } from "@cadmakers-ui/icons";
import { Input } from "reactstrap";
import { Application } from "../../App";
import { OverviewCheckItem } from "../../Common/Components/CheckItem/OverviewCheckItem";
import { Topic } from "../../Topic/Models/Topic";
import styles from "../Styles/OverviewSideBar.module.scss";
import { FeedbackCategory } from "../../Feedback/Constants/feedbackEnums";

type Props = {
  isOpen: boolean;
  categories: any[];
  applications: Application[];
  topics: Topic[];
  onChangeSearch: (query: string) => void;
  onSubmit: (categories: any[], applicationIds: number[], topicIds: number[]) => void;
};

const OverviewSideBar: React.FC<Props> = props => {
  const [categoriesCheckedState, setCategoriesCheckedState] = useState(
    new Array(props.categories.length).fill(false)
  );

  const [applicationsCheckedState, setApplicationsCheckedState] = useState(
    new Array(props.applications.length).fill(false)
  );

  const [topicsCheckedState, setTopicsCheckedState] = useState(
    new Array(props.topics.length).fill(false)
  );

  const [searchValue, setSearchValue] = useState("");

  const [showCategories, setShowCategories] = useState(true);
  const [showApplications, setShowApplications] = useState(true);
  const [showTopics, setShowTopics] = useState(true);

  const onClickCategoryCheckbox = (position: number) => {
    const updatedCategoryCheckedState = categoriesCheckedState.map((item, index) =>
      index === position ? !item : item
    );

    setCategoriesCheckedState(updatedCategoryCheckedState);
  };

  const onClickApplicationCheckbox = (position: number) => {
    const updatedApplicationCheckedState = applicationsCheckedState.map((item, index) =>
      index === position ? !item : item
    );
    setApplicationsCheckedState(updatedApplicationCheckedState);
  };

  const onClickTopicCheckbox = (position: number) => {
    const updatedTopicCheckedState = topicsCheckedState.map((item, index) =>
      index === position ? !item : item
    );

    setTopicsCheckedState(updatedTopicCheckedState);
  };

  const categoryCheckItems = props.categories.map((category, index) => {
    return (
      <OverviewCheckItem
        checked={categoriesCheckedState[index]}
        label={category.label}
        onChange={onClickCategoryCheckbox}
        index={index}
        key={category.value}
      />
    );
  });

  const applicationCheckItems = props.applications.map((application, index) => {
    return (
      <OverviewCheckItem
        checked={applicationsCheckedState[index]}
        label={application.name}
        onChange={onClickApplicationCheckbox}
        index={index}
        key={application.authId}
      />
    );
  });

  const topicCheckItems = props.topics.map((topic, index) => {
    return (
      <OverviewCheckItem
        checked={topicsCheckedState[index]}
        label={topic.name}
        onChange={onClickTopicCheckbox}
        index={index}
        key={topic.code}
      />
    );
  });

  const onChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    props.onChangeSearch(e.target.value);
  };

  const resetFilters = () => {
    setCategoriesCheckedState(new Array(props.categories.length).fill(false));
    setApplicationsCheckedState(new Array(props.applications.length).fill(false));
    setTopicsCheckedState(new Array(props.topics.length).fill(false));
  };

  const getCheckedCategories = () => {
    const checkedCategories = [] as FeedbackCategory[];
    categoriesCheckedState.forEach((element, index) => {
      if (element) {
        checkedCategories.push(props.categories[index]);
      }
    });
    return checkedCategories;
  };

  const getCheckedApplications = () => {
    const checkedApplications = [] as number[];

    applicationsCheckedState.forEach((element, index) => {
      if (element) {
        checkedApplications.push(props.applications[index].id);
      }
    });

    return checkedApplications;
  };

  const getCheckedTopics = () => {
    const checkedTopics = [] as number[];

    topicsCheckedState.forEach((element, index) => {
      if (element) {
        checkedTopics.push(props.topics[index].id);
      }
    });

    return checkedTopics;
  };

  const onSubmit = () => {
    props.onSubmit(getCheckedCategories(), getCheckedApplications(), getCheckedTopics());
  };

  return (
    <div className={props.isOpen ? styles.sidebar : styles.hidden}>
      <div className={styles.header}>
        <div className={styles.title}>Apps & Topics</div>
        <Input
          className={styles.searchbar}
          value={searchValue}
          onChange={onChangeSearch}
          type="search"
          placeholder="Search..."
        />
        <PrimaryButton
          application="cmThread"
          neutral
          type="generic"
          text="Reset Filters"
          onClick={resetFilters}
        />
      </div>
      <div className={styles.content}>
        <div className={styles.sectionlabel}>
          <div>Feedback Category</div>

          {showCategories ? (
            <Icon
              className={styles.downarrowicon}
              appName="GeneralLayout"
              assetName="GENERAL_L_ArrowDown"
              onClick={e => {
                setShowCategories(false);
              }}
            />
          ) : (
            <Icon
              className={styles.sidearrowicon}
              appName="GeneralLayout"
              assetName="GENERAL_L_ArrowRight"
              onClick={e => {
                setShowCategories(true);
              }}
            />
          )}
        </div>
        <div className={styles.checkitems}>{showCategories && categoryCheckItems}</div>
        <div className={styles.sectionlabel}>
          <div>Applications</div>
          {showApplications ? (
            <Icon
              className={styles.downarrowicon}
              appName="GeneralLayout"
              assetName="GENERAL_L_ArrowDown"
              onClick={e => {
                setShowApplications(false);
              }}
            />
          ) : (
            <Icon
              className={styles.sidearrowicon}
              appName="GeneralLayout"
              assetName="GENERAL_L_ArrowRight"
              onClick={e => {
                setShowApplications(true);
              }}
            />
          )}
        </div>
        <div className={styles.checkitems}>{showApplications && applicationCheckItems}</div>
        <div className={styles.sectionlabel}>
          <div>Topics</div>
          {showTopics ? (
            <Icon
              className={styles.downarrowicon}
              appName="GeneralLayout"
              assetName="GENERAL_L_ArrowDown"
              onClick={e => {
                setShowTopics(false);
              }}
            />
          ) : (
            <Icon
              className={styles.sidearrowicon}
              appName="GeneralLayout"
              assetName="GENERAL_L_ArrowRight"
              onClick={e => {
                setShowTopics(true);
              }}
            />
          )}
        </div>
        <div className={styles.checkitems}>{showTopics && topicCheckItems}</div>
      </div>
      <div className={styles.footer}>
        <PrimaryButton application="cmThread" type="generic" text="Apply" onClick={onSubmit} />
      </div>
    </div>
  );
};
export default OverviewSideBar;
